import React from 'react';
import { UserPlus, ShieldCheck, Send, ShieldAlert, CheckCircle2, XCircle, History } from 'lucide-react';
import type { LabResult } from './labUtils';

interface AuditEvent {
    id: string;
    label: string;
    actor?: string;
    timestamp?: string;
    detail?: string;
    Icon: React.ElementType;
    color: string;
}

// Read-only timeline over LabResult fields that are already captured by the app (enteredBy,
// verifiedBy/verifiedAt, submittedAt, approvedBy/approvedAt, rejectionReason,
// criticalAcknowledgedBy/At) — no new persistence, purely a view over real, existing data.
const buildEvents = (result: LabResult): AuditEvent[] => {
    const r = result as any;
    const events: AuditEvent[] = [];

    if (r.enteredBy) {
        events.push({ id: 'entered', label: 'Draft Entered', actor: r.enteredBy, timestamp: r.createdAt, Icon: UserPlus, color: '#6B7280' });
    }
    if (r.criticalAcknowledgedBy) {
        events.push({
            id: 'critical', label: 'Critical Value Acknowledged', actor: r.criticalAcknowledgedBy,
            timestamp: r.criticalAcknowledgedAt, detail: r.criticalNote, Icon: ShieldAlert, color: '#DC2626',
        });
    }
    if (r.verifiedBy) {
        events.push({ id: 'verified', label: 'Verified', actor: r.verifiedBy, timestamp: r.verifiedAt, Icon: ShieldCheck, color: '#059669' });
    }
    if (r.submittedAt) {
        events.push({ id: 'submitted', label: 'Submitted for Review', timestamp: r.submittedAt, Icon: Send, color: '#2563EB' });
    }
    if (r.approvedBy && (r.reportStatus === 'approved' || r.status === 'approved')) {
        events.push({ id: 'approved', label: 'Approved & Signed', actor: r.approvedBy, timestamp: r.approvedAt, Icon: CheckCircle2, color: '#059669' });
    }
    if (r.rejectionReason && (r.reportStatus === 'rejected' || r.status === 'rejected')) {
        events.push({
            id: 'rejected', label: 'Rejected', timestamp: r.updatedAt, detail: r.rejectionReason,
            Icon: XCircle, color: '#DC2626',
        });
    }

    return events
        .filter(e => e.timestamp)
        .sort((a, b) => new Date(a.timestamp!).getTime() - new Date(b.timestamp!).getTime());
};

export const ResultAuditTrail: React.FC<{ result: LabResult | null }> = ({ result }) => {
    if (!result) return null;
    const events = buildEvents(result);
    if (events.length === 0) return null;

    return (
        <div className="px-4 py-3 flex flex-col gap-2.5">
            <div className="flex items-center gap-1.5">
                <History className="w-3.5 h-3.5 text-[var(--v2-text-muted)]" strokeWidth={2} />
                <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--v2-text-muted)] m-0">Audit Trail</p>
            </div>
            <div className="flex flex-col gap-3">
                {events.map((e, i) => (
                    <div key={e.id} className="flex items-start gap-2.5">
                        <div className="flex flex-col items-center flex-shrink-0">
                            <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: `${e.color}18` }}>
                                <e.Icon className="w-3 h-3" style={{ color: e.color }} strokeWidth={2.5} />
                            </div>
                            {i < events.length - 1 && <div className="w-px flex-1 mt-1" style={{ background: 'var(--v2-border-subtle)', minHeight: 16 }} />}
                        </div>
                        <div className="min-w-0 pb-1">
                            <p className="text-[11px] font-bold text-[var(--v2-text-primary)] m-0">{e.label}</p>
                            <p className="text-[10px] text-[var(--v2-text-muted)] m-0">
                                {e.actor ? `${e.actor} · ` : ''}{new Date(e.timestamp!).toLocaleString('en-IN')}
                            </p>
                            {e.detail && <p className="text-[10px] text-[var(--v2-text-secondary)] m-0 mt-0.5 italic">"{e.detail}"</p>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ResultAuditTrail;
